import React, { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Drawer, Stack } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import PageIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FolderIcon from "@mui/icons-material/FolderOutlined";
import PagesOverviewIcon from "@mui/icons-material/LayersOutlined";
import PublishedIcon from "@mui/icons-material/PublishedWithChanges";
import SegmentIcon from "@mui/icons-material/Segment";
import { green, orange } from "@mui/material/colors";
import CheckIcon from "@mui/icons-material/Check";
import { Apps } from "@mui/icons-material";

import { useTranslation } from "@/lib/i18n";
import { useAdminDashboard } from "@/features/admin/hooks";
import { useDashboardNavigationElements } from "@/features/admin/hooks/queries";
import { routes } from "@/features/shared/navigation";
import useMedia from "@/features/admin/hooks/useMedia";
import { Icon } from "@/components/icon/icon";
import {
  isBuiltInPageKind,
  navigationElementKind,
  WARNING_PAGE_ICON,
  withoutHiddenWarningPages,
} from "@/lib/navigation-element-kinds";
import { useWarningPageNavLabel } from "@/features/shared/hooks/use-warning-page-defaults";
import { useWarningPageVisibility } from "@/features/admin/hooks/use-warning-page-visibility";

import { transformToParentChildrenTreeData, hasActiveChildPage } from "./helpers";
import { StyledIcon, StyledList } from "./styled";
import Item from "./item";

export const drawerWidth = 290;

type Props = {
  onOpenPublicationDialog: () => void;
};

export default function ResponsiveDrawer({ onOpenPublicationDialog }: Props) {
  const { t } = useTranslation();
  const navLabel = useWarningPageNavLabel();
  const params = useParams();
  const [open, setOpen] = React.useState(false);

  const dashboard = useAdminDashboard();
  const navigationElements = useDashboardNavigationElements(dashboard.id).data;
  const visibility = useWarningPageVisibility(dashboard.id);
  const isDesktop = useMedia("(min-width: 960px)");

  const dashboardHasChanges = dashboard.has_unpublished_changes;

  const parentsChildrenTreeData = useMemo(() => {
    return transformToParentChildrenTreeData(
      withoutHiddenWarningPages(navigationElements ?? [], visibility),
    );
  }, [navigationElements, visibility.publicWarningsPage, visibility.maritimeWarningsPage]);

  const handleOnClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleOpenPublicationDialog = useCallback(() => {
    setOpen(false);
    onOpenPublicationDialog();
  }, [onOpenPublicationDialog]);

  return (
    <>
      <StyledIcon color="primary" onClick={() => setOpen(!open)}>
        <Apps />
      </StyledIcon>

      <Drawer
        PaperProps={{
          sx: {
            position: "relative",
            boxShadow: "8px 0px 19px -12px rgb(0 0 0 / 8%)",
            border: 0,
          },
        }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
        }}
        variant={isDesktop ? "permanent" : "temporary"}
        open={open}
        onClose={handleOnClose}
      >
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore: MUI seems to not accept the component prop, but it does work */}
        <nav>
          <StyledList>
            <Item
              sx={{ mb: 2, mt: 1 }}
              key="back"
              route={routes.admin.home}
              icon={<ArrowBackIcon />}
              name={t("admin.side_menu.back_to_overview")}
            />

            <Item
              key="publication"
              arrow
              open={!!params.page}
              subText={
                dashboardHasChanges ? (
                  t("admin.side_menu.publication_pending_changes")
                ) : (
                  <Stack direction="row" alignItems="center" gap={0}>
                    <CheckIcon sx={{ fontSize: 14 }} />
                    {t("admin.side_menu.publication_no_changes")}
                  </Stack>
                )
              }
              onClick={handleOpenPublicationDialog}
              icon={<PublishedIcon />}
              name={t("admin.side_menu.publication_overview")}
              sx={{
                ".MuiListItemText-secondary": {
                  color: dashboardHasChanges ? orange[900] : green[900],
                },
              }}
            />

            <Item
              key="pages-overview"
              arrow
              open={!!params.page}
              route={routes.admin.dashboard_pages_overview}
              icon={<SegmentIcon />}
              name={t("admin.side_menu.pages_overview")}
            />

            <Item
              key="pages"
              arrow
              open={true}
              icon={<PagesOverviewIcon />}
              name={t("admin.side_menu.pages")}
            >
              {parentsChildrenTreeData.map((page) => {
                const isBuiltIn = isBuiltInPageKind(navigationElementKind(page));
                const isFolder = !page.page_id && !isBuiltIn;
                const icon = isBuiltIn ? (
                  <Icon name={WARNING_PAGE_ICON} />
                ) : isFolder ? (
                  <FolderIcon />
                ) : (
                  <PageIcon />
                );

                return (
                  <Item
                    icon={icon}
                    route={page.page_id ? `page/${page.page_id}` : undefined}
                    open={hasActiveChildPage(page.children, params.page)}
                    depth={1}
                    key={page.id}
                    name={navLabel(page)}
                  >
                    {(page.children || []).map((subPage) => (
                      <Item
                        icon={<PageIcon />}
                        route={`page/${subPage.page_id}`}
                        depth={2}
                        key={subPage.id}
                        name={subPage.title}
                      />
                    ))}
                  </Item>
                );
              })}
            </Item>

            <Item
              arrow
              route={routes.admin.dashboard_settings}
              key="settings"
              icon={<SettingsIcon />}
              name={t("admin.side_menu.settings")}
            />
          </StyledList>
        </nav>
      </Drawer>
    </>
  );
}
